import React from "react";
import styled from "styled-components";
import { colors } from "utils";

// Shared per-question metric cells used by both the live pill's Live Updates
// rows and the historical Session Report "Group Results". Tokens mirror
// LiveSessionPill's `T` so the historical view renders identically to live.
const TOKENS = {
  green600: "#00A35A",
  green50: "#F0FDF4",
  yellow600: "#CA8A04",
  yellow300: "#FDE047",
  yellow50: "#FEFCE8",
  pink500: colors.pink500,
  pink300: colors.pink300,
  pink50: colors.pink50,
  blue800: colors.blue800,
  blue300: colors.blue300,
  blue50: "#EEEEF9",
  offWhite: "#FAF7F2",
  white: "#FFFFFF",
};

// level (0–4 | null) → palette bucket. null = not scored yet (pending).
export const collabBucket = (level) => {
  if (level == null) return "unknown";
  if (level <= 1) return "low";
  if (level === 2) return "mid";
  if (level === 3) return "high";
  return "top";
};

const CheckIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
    <path
      d="M5 12.5l4 4 10-10"
      fill="none"
      stroke={TOKENS.white}
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const XIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
    <path
      d="M6 6l12 12M18 6L6 18"
      fill="none"
      stroke={TOKENS.white}
      strokeWidth="3"
      strokeLinecap="round"
    />
  </svg>
);

const Circle = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${({ $bg }) => $bg};
  border: 1px solid ${({ $border }) => $border};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PendingDot = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${TOKENS.offWhite};
  border: 1px solid #b9b7b3;
`;

// The verdict beside one answer in the Answer & Survey table: the same green
// check / pink X the Accuracy row uses, sized to sit next to the answer's
// own text inside a table cell.
const MarkCircle = styled.span`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${({ $bg }) => $bg};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 6px;
  vertical-align: text-bottom;
`;

// correct: true → green check, false → pink X, null/undefined → nothing —
// an attempt recorded before verdicts were tracked makes no claim either way.
export const AnswerMark = ({ correct }) => {
  if (correct == null) return null;
  return correct ? (
    <MarkCircle $bg={TOKENS.green600} role="img" aria-label="Correct answer">
      <CheckIcon size={10} />
    </MarkCircle>
  ) : (
    <MarkCircle $bg={TOKENS.pink500} role="img" aria-label="Incorrect answer">
      <XIcon size={10} />
    </MarkCircle>
  );
};

export const UNFINISHED_LABEL =
  "Not applicable — the session ended before the group finished this question";

const NaCircle = styled(Circle)`
  color: #6b6967;
  font-size: 9px;
  font-weight: 700;
`;

// aligned: true → correct (green check), false → incorrect (pink X),
// null/undefined → not scored yet (neutral dot). `unfinished` wins over all of
// them: the session ended before the group finished the question.
export const ProficiencyCell = ({ aligned, unfinished = false }) => {
  if (unfinished === true) {
    return (
      <NaCircle
        $bg={TOKENS.offWhite}
        $border="#B9B7B3"
        role="img"
        aria-label={UNFINISHED_LABEL}
        title={UNFINISHED_LABEL}
      >
        N/A
      </NaCircle>
    );
  }
  if (aligned == null) {
    return <PendingDot title="Accuracy score pending" />;
  }
  return aligned ? (
    <Circle
      $bg={TOKENS.green600}
      $border={TOKENS.green600}
      title="Correct: all students submitted the correct answer"
    >
      <CheckIcon />
    </Circle>
  ) : (
    <Circle
      $bg={TOKENS.pink500}
      $border={TOKENS.pink500}
      title="Incorrect: one or more students did not submit the correct answer"
    >
      <XIcon />
    </Circle>
  );
};

// level: 0–4 | null. When `na` is true, renders the "N/A" hex (group solved in
// solo time). null level with na=false renders the empty/pending hex.
// `unfinished` wins over both: the "N/A" hex, named and titled with the reason.
export const CollabHex = ({ level, na = false, unfinished = false }) => {
  const isUnfinished = unfinished === true;
  const bucket = isUnfinished || na ? "na" : collabBucket(level);
  const palette = {
    low:  { fill: TOKENS.pink50,  border: TOKENS.pink300,  text: TOKENS.pink500,  fontSize: 12 },
    mid:  { fill: TOKENS.yellow50, border: TOKENS.yellow300, text: TOKENS.yellow600, fontSize: 12 },
    high: { fill: TOKENS.green50, border: "#76D9AD",        text: TOKENS.green600, fontSize: 12 },
    top:  { fill: TOKENS.blue50,  border: TOKENS.blue300,   text: TOKENS.blue800,  fontSize: 12 },
    na: { fill: TOKENS.offWhite, border: "#B9B7B3", text: "#6B6967", fontSize: 9 },
    unknown: { fill: TOKENS.offWhite, border: "#B9B7B3", text: null, fontSize: 12 },
  };
  const p = palette[bucket] || palette.unknown;
  const label =
    bucket === "na"
      ? "N/A"
      : p.text != null && level != null
        ? String(level === 0 ? 1 : level)
        : null;
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 32 32"
      role="img"
      aria-label={
        isUnfinished
          ? UNFINISHED_LABEL
          : bucket === "na"
            ? "Not applicable — group solved in solo time"
            : level != null
              ? `Collaboration level ${level === 0 ? 1 : level}`
              : "Collaboration score pending"
      }
    >
      {isUnfinished && <title>{UNFINISHED_LABEL}</title>}
      <polygon
        points="16,2 29,9.5 29,22.5 16,30 3,22.5 3,9.5"
        fill={p.fill}
        stroke={p.border}
        strokeWidth="1.5"
      />
      {label != null && (
        <text
          x="16"
          y={bucket === "na" ? 19 : 20}
          textAnchor="middle"
          fontFamily="Inter, sans-serif"
          fontSize={p.fontSize}
          fontWeight="700"
          fill={p.text}
        >
          {label}
        </text>
      )}
    </svg>
  );
};
